import './SelectboxCellRenderer.scss';

import WarningIcon from '@mui/icons-material/Warning';
import { MenuItem, Stack } from '@mui/material';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { includes } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useCallback } from 'react';
import { SelectboxCellRendererProps } from 'types/ScenarioManagement.types';

const DEFAULT_UNMATCHED_WARNING_TEXT = 'This selected value does not match any available options';

// TODO: Move Cell Renderers to a shared path for reusability
const SelectboxCellRenderer = (props: SelectboxCellRendererProps) => {
  const { readonly, options, onChange, data, value, colDef } = props;

  /**
   * Handle change event
   * @param event
   */
  const handleChange = (event: SelectChangeEvent, _child: ReactNode) => {
    if (onChange) {
      onChange(data?.metadata?.internalId, colDef!.field!, event.target.value);
    }
  };

  const renderMenuItems = useCallback(() => {
    const menuItems = [];

    // Handling when `value` doesn't match available `options`
    if (!isEmpty(value) && !includes(options, value)) {
      menuItems.push(
        <MenuItem key={value} value={value}>
          <ListItemText secondary={DEFAULT_UNMATCHED_WARNING_TEXT}>
            <Typography color='warning'>{value}</Typography>
          </ListItemText>
          <ListItemIcon>
            <WarningIcon fontSize='small' color='warning' />
          </ListItemIcon>
        </MenuItem>,
      );
      menuItems.push(<Divider key='unmatched-divider' />);
    }

    // Render available options
    menuItems.push(
      ...options.map((option, index) => (
        <MenuItem key={[index, option].join('-')} value={option}>
          {option}
        </MenuItem>
      )),
    );

    return menuItems;
  }, [options, value]);

  const renderValue = useCallback(
    (val?: string) => {
      return (
        <Stack direction='row' className='selectbox-render-value' spacing={1}>
          <Typography>{value}</Typography>
          {val && !includes(options, value) && (
            <Tooltip title={DEFAULT_UNMATCHED_WARNING_TEXT}>
              <WarningIcon color='warning' />
            </Tooltip>
          )}
        </Stack>
      );
    },
    [options, value],
  );

  // only return the value in readonly mode...
  if (readonly) {
    return value;
  }

  // ...otherwise, allow the user to change the value (bypassing AGGrid's built-in edit mode)
  return (
    <Select
      value={value as string}
      onChange={handleChange}
      fullWidth
      className='ag-grid-mui-select'
      renderValue={renderValue}
    >
      {renderMenuItems()}
    </Select>
  );
};

export default SelectboxCellRenderer;
